/**
 * Crop-thumb aspect ratios + shapes that match live template CSS frames.
 * Pass these into BannerImageFrameControl / WebsiteEditorImageEditField so the
 * edit-drawer preview mirrors what the frontend shows.
 */
export const IMAGE_FRAME_ASPECT = {
  /** Bio / LiB avatar, About headshot, link thumbs, schema gallery cells. */
  square: "1 / 1",
  /**
   * LiB My Links grid card image — matches GridTemplate
   * (`--content-square-size` × `--grid-image-height` = 143∶92).
   */
  linkGrid: "143 / 92",
  /** Highlight cards (`.mte-tpl-hlc-media`). */
  highlightCard: "16 / 9",
  /** About "More From" card heroes (`.mte-tpl-av-hero`). */
  moreFromCard: "4 / 5",
  /** Blog / wide gallery / fullbleed-style landscape. */
  landscape: "16 / 9",
  /** Magazine-style portrait bio image. */
  portrait: "4 / 5",
  /** Legacy Stay banner recommend size — colored / ribbon styles. */
  bannerLegacy: "657 / 350",
} as const;

export type ImageFrameAspect =
  (typeof IMAGE_FRAME_ASPECT)[keyof typeof IMAGE_FRAME_ASPECT];

/** Outer mask of the crop preview — must match the live image container. */
export type ImageFrameShape = "circle" | "rounded" | "rect";

/** Photo-ava modifier class matching the live crop aspect + shape. */
export function photoAvaClassForAspect(
  aspectRatio?: string,
  opts?: { shape?: ImageFrameShape },
): string {
  if (opts?.shape === "circle") return "mte-we-photo-ava--round";
  const a = String(aspectRatio || "").replace(/\s+/g, "");
  if (!a || a === "1/1" || a === "1") return "mte-we-photo-ava--square";
  if (a === "4/5" || a === "3/4") return "mte-we-photo-ava--portrait";
  if (a === "5/4" || a === "4/3") return "mte-we-photo-ava--split";
  if (a === "16/9" || a === "657/350" || a === "143/92") {
    return "mte-we-photo-ava--landscape";
  }
  return "mte-we-photo-ava--landscape";
}

/** Crop-preview modifier class matching the live image mask. */
export function cropShapeClass(shape?: ImageFrameShape): string {
  if (shape === "circle") return "mte-we-ban-crop--circle";
  if (shape === "rect") return "mte-we-ban-crop--rect";
  return "";
}

/**
 * Home / LiB hero photo frame — matches live BioSection CSS for each layout.
 * LiB profile avatars are always circular.
 */
export function bioPhotoFrameSpec(
  bioStyle: string | undefined,
  opts?: { linkInBio?: boolean },
): { aspectRatio: string; shape: ImageFrameShape } {
  if (opts?.linkInBio) {
    return { aspectRatio: IMAGE_FRAME_ASPECT.square, shape: "circle" };
  }
  switch (String(bioStyle || "center").trim()) {
    case "magazine":
      return { aspectRatio: IMAGE_FRAME_ASPECT.portrait, shape: "rounded" };
    case "hero":
      // Full-bleed background cover — no circular mask.
      return { aspectRatio: IMAGE_FRAME_ASPECT.landscape, shape: "rect" };
    default:
      // center, split, card, minimal, stacked-large → circular avatar
      return { aspectRatio: IMAGE_FRAME_ASPECT.square, shape: "circle" };
  }
}
